/**
 * TaskDetailModal — opened on double-click of a Gantt row.
 * Renders ALL fields (visible + hidden + custom).
 *
 * Group keys are translated at render time so language switching applies
 * instantly. Field codes (task_id, start, ...) stay untranslated because
 * they are the canonical schema keys exposed by the parser.
 */
import { X } from "lucide-react";
import { useI18n } from "../context/I18nContext";

const FIELD_GROUPS = [
  { key: "hierarchy", labelKey: "task_section_hierarchy", fields: ["task_id", "task_uid", "wbs", "outline_level", "is_summary", "milestone", "parent_uid"] },
  { key: "dates",     labelKey: "task_section_dates",     fields: ["start", "finish", "duration_days", "deadline", "constraint_type", "constraint_date"] },
  { key: "actuals",   labelKey: "task_section_actuals",   fields: ["actual_start", "actual_finish", "actual_duration", "percent_complete", "percent_work_complete", "status"] },
  { key: "baseline",  labelKey: "task_section_baseline",  fields: ["baseline_start", "baseline_finish", "baseline_duration"] },
  { key: "cpm",       labelKey: "task_section_cpm",       fields: ["early_start", "early_finish", "late_start", "late_finish", "free_slack", "total_slack", "is_critical"] },
  { key: "resources", labelKey: "task_section_resources", fields: ["resource_names", "resources", "cost", "actual_cost", "work_days", "actual_work_days"] },
  { key: "meta",      labelKey: "task_section_meta",      fields: ["priority", "active", "location", "notes"] },
];

function formatVal(v, t) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? t("yes_short") : t("no_short");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function TaskDetailModal({ task, onClose }) {
  const { t } = useI18n();
  if (!task) return null;
  const customEntries = Object.entries(task.custom_fields || {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" data-testid="task-detail-modal">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="cs-label">{t("task_section_label")}</div>
            <h3 className="font-heading text-lg font-semibold text-zinc-950 truncate" data-testid="task-detail-title">
              [{task.task_id}] {task.name}
            </h3>
            {task.wbs && <div className="font-mono text-[11px] text-zinc-500">{task.wbs}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100" data-testid="task-detail-close">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6 scrollbar-thin">
          {FIELD_GROUPS.map((g) => (
            <section key={g.key} className="mb-5">
              <div className="cs-label mb-2">{t(g.labelKey)}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                {g.fields.map((f) => (
                  <div key={f} className="flex items-baseline justify-between border-b border-zinc-50 py-1">
                    <span className="text-zinc-500 font-mono">{f}</span>
                    <span className="text-zinc-900 text-right ml-2 max-w-[60%] truncate" title={formatVal(task[f], t)}>{formatVal(task[f], t)}</span>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {customEntries.length > 0 && (
            <section className="mb-5">
              <div className="cs-label mb-2">{t("task_section_custom")}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                {customEntries.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between border-b border-zinc-50 py-1">
                    <span className="text-zinc-500 font-mono">{k}</span>
                    <span className="text-zinc-900 text-right ml-2 max-w-[60%] truncate" title={formatVal(v, t)}>{formatVal(v, t)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {task.predecessors?.length > 0 && (
            <section>
              <div className="cs-label mb-2">{t("task_predecessors")}</div>
              <ul className="text-xs space-y-1">
                {task.predecessors.map((p, i) => (
                  <li key={`${p.task_uid}-${p.type}-${i}`} className="font-mono text-zinc-700">→ UID {p.task_uid} ({p.type}) {p.lag_days ? `· lag ${p.lag_days}d` : ""}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
